import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import styles from '../DescriptionEditor.module.scss';
import useEditorChromeSync from '../hooks/useEditorChromeSync';
import useLinkMode from '../hooks/useLinkMode';
import useUiMotion from '../hooks/useUiMotion';
import { clearFormatting } from '../utils/editorHelpers';
import { LinkIcon } from './Icons';
import LinkPanel from './LinkPanel';

/**
 * Gmail-style formatting chrome.
 * [Aa] reveals B/I/S/Clear strip; [🔗] opens a link panel.
 *
 * Desktop (`layout="dock"`): the link panel is portaled to document.body and
 * anchored to the cursor/selection position, captured once on open.
 * Mobile (`layout="fixed"`): the link panel renders inline in the fixed
 * toolbar above the keyboard (caret coords are unreliable while the keyboard
 * is opening). `layout="inline"` is used by comment variants.
 */
export default function FormatChrome({
  editor,
  cancelBlur,
  layout,
  fixedBottomPx = 0,
  onLinkModeChange,
}) {
  const [formattingExpanded, setFormattingExpanded] = useState(false);
  const uiMotion = useUiMotion();

  useEditorChromeSync(editor);

  const collapseFormatting = useCallback(() => {
    setFormattingExpanded(false);
  }, []);

  const {
    linkMode,
    linkUrl,
    linkText,
    anchorStyle,
    selectionEmpty,
    canApplyLink,
    linkTextInputRef,
    linkUrlInputRef,
    panelRef,
    setLinkUrl,
    setLinkText,
    openLinkMode,
    cancelLinkMode,
    applyLink,
  } = useLinkMode({
    editor,
    layout,
    onLinkModeChange,
    onCollapseFormatting: collapseFormatting,
  });

  // For mobile: keep toolbar alive while link inputs are focused.
  const linkBlurProps =
    layout === 'fixed' || layout === 'inline' ? { onFocus: cancelBlur } : {};

  const linkPanelProps = {
    selectionEmpty,
    linkUrl,
    linkText,
    setLinkUrl,
    setLinkText,
    linkUrlInputRef,
    linkTextInputRef,
    linkBlurProps,
    canApplyLink,
    applyLink,
    cancelLinkMode,
  };

  // Desktop: link panel portaled near the cursor; backdrop + card animate in/out.
  const dockedLinkPortal =
    layout === 'dock' &&
    createPortal(
      <AnimatePresence mode="sync">
        {linkMode && anchorStyle && (
          <>
            <motion.div
              key="link-backdrop"
              aria-hidden="true"
              className={styles.linkBackdropMotion}
              style={{ position: 'fixed', inset: 0, zIndex: 10049 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={uiMotion.quick}
              onPointerDown={(e) => {
                e.stopPropagation();
                cancelLinkMode();
              }}
            />
            <motion.div
              key="link-card"
              ref={panelRef}
              style={{ position: 'fixed', zIndex: 10050, ...anchorStyle }}
              className={`${styles.chromeLinkCard} ${styles.linkCardMotion}`}
              data-link-ui=""
              initial={{ opacity: 0, scale: 0.96, y: 6 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: 4 }}
              transition={uiMotion.pop}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <LinkPanel {...linkPanelProps} />
            </motion.div>
          </>
        )}
      </AnimatePresence>,
      document.body
    );

  const primaryRow = (
    <div className={styles.chromePrimaryRow}>
      <button
        type="button"
        className={`${styles.chromeAa} ${
          formattingExpanded ? styles.chromeAaActive : ''
        }`}
        aria-expanded={formattingExpanded}
        aria-label="Text formatting"
        title="Show or hide bold, italic, and strikethrough"
        onMouseDown={(e) => {
          e.preventDefault();
          setFormattingExpanded((v) => !v);
        }}
      >
        Aa
      </button>
      <button
        type="button"
        className={`${styles.chromeLinkBtn} ${
          linkMode || editor.isActive('link') ? styles.chromeBtnActive : ''
        }`}
        aria-label={editor.isActive('link') ? 'Remove link' : 'Add link'}
        title="Link"
        onMouseDown={(e) => {
          e.preventDefault();
          if (linkMode) {
            cancelLinkMode();
          } else if (editor.isActive('link')) {
            editor.chain().focus().unsetLink().run();
          } else {
            openLinkMode();
          }
        }}
      >
        <LinkIcon />
      </button>
    </div>
  );

  const ChromeRoot =
    layout === 'fixed' || layout === 'inline' ? motion.div : 'div';
  const chromeRootMotion =
    layout === 'fixed' || layout === 'inline'
      ? {
          initial: { opacity: 0, y: 22 },
          animate: { opacity: 1, y: 0 },
          exit: { opacity: 0, y: 16 },
          transition: uiMotion.pop,
        }
      : {};

  const chrome = (
    <ChromeRoot
      className={`${styles.formatChrome} ${
        layout === 'fixed'
          ? styles.formatChromeFixed
          : layout === 'inline'
            ? styles.formatChromeInline
            : styles.formatChromeDock
      } ${
        layout === 'fixed' || layout === 'inline' ? styles.chromeMotionFixed : ''
      }`}
      style={layout === 'fixed' ? { bottom: fixedBottomPx } : undefined}
      onMouseDown={(e) => e.preventDefault()}
      {...chromeRootMotion}
    >
      <AnimatePresence initial={false}>
        {formattingExpanded &&
          !(linkMode && (layout === 'fixed' || layout === 'inline')) && (
            <motion.div
              key="format-strip"
              className={`${styles.chromeFormatRow} ${styles.formatStripMotion}`}
              role="toolbar"
              aria-label="Text formatting"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={uiMotion.fade}
            >
              <button
                type="button"
                className={`${styles.chromeFmtBtn} ${
                  editor.isActive('bold') ? styles.chromeBtnActive : ''
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor.chain().focus().toggleBold().run();
                }}
                aria-label="Bold"
              >
                <strong>B</strong>
              </button>
              <button
                type="button"
                className={`${styles.chromeFmtBtn} ${
                  editor.isActive('italic') ? styles.chromeBtnActive : ''
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor.chain().focus().toggleItalic().run();
                }}
                aria-label="Italic"
              >
                <em>I</em>
              </button>
              <button
                type="button"
                className={`${styles.chromeFmtBtn} ${
                  editor.isActive('strike') ? styles.chromeBtnActive : ''
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor.chain().focus().toggleStrike().run();
                }}
                aria-label="Strikethrough"
              >
                <s>S</s>
              </button>
              <div className={styles.chromeDivider} />
              <button
                type="button"
                className={styles.chromeFmtBtn}
                onMouseDown={(e) => {
                  e.preventDefault();
                  clearFormatting(editor);
                }}
                aria-label="Clear formatting"
                title="Clear formatting (⌘⇧M)"
              >
                Clear
              </button>
            </motion.div>
          )}
      </AnimatePresence>
      {/* Mobile: link panel replaces the primary row inline.
          Desktop: primary row is always visible; the link panel is portaled. */}
      {layout === 'fixed' || layout === 'inline' ? (
        <AnimatePresence initial={false} mode="wait">
          {linkMode ? (
            <motion.div
              key="link-inline"
              className={`${styles.chromeLinkPanel} ${styles.chromeLinkPanelMotion}`}
              data-link-ui=""
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={uiMotion.pop}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <LinkPanel {...linkPanelProps} />
            </motion.div>
          ) : (
            <motion.div
              key="primary-row"
              className={styles.chromePrimaryRowWrap}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={uiMotion.fade}
            >
              {primaryRow}
            </motion.div>
          )}
        </AnimatePresence>
      ) : (
        primaryRow
      )}
    </ChromeRoot>
  );

  return (
    <>
      {layout === 'fixed' ? createPortal(chrome, document.body) : chrome}
      {dockedLinkPortal}
    </>
  );
}
